import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clapperboard, Swords, Trophy } from "lucide-react";
import {
  formatClubRankingLabel,
  formatKotPlayerDisplayName,
  isPlayableKotMatchVideoUrl,
  kotLeaderboardGamePointsScored,
  kotLeaderboardPointSpread,
  sortKotLeaderboardByKotPoints,
  sortKotLeaderboardByPointSpread,
} from "../utils/kotPlayerMatches";
import "./PlayerSessionResultsDialog.css";

function outcomeLabel(row, t) {
  switch (row.outcome) {
    case "win":
      return t("kotPlayerMatchResultWin", "Won");
    case "loss":
      return t("kotPlayerMatchResultLoss", "Lost");
    case "abandoned":
      return t("kotMatchAbandoned", "Aband.");
    case "pending":
      return t("kotPlayerMatchResultPending", "Pending");
    default:
      return "—";
  }
}

function getMatchMessage(row, t) {
  if (row.outcome === "abandoned" && row.abandoned_reason) {
    const reasonLower = String(row.abandoned_reason).toLowerCase().trim();
    if (reasonLower.startsWith("stopped") || reasonLower.includes("stopped by")) {
      return null;
    }
    if (reasonLower === "player_swapped") {
      return t("kotMatchAbandonedPlayerSwapped", "Player swapped during match");
    }
    if (reasonLower === "exercise_cancelled") {
      return t("kotMatchAbandonedExerciseCancelled", "Exercise cancelled");
    }
    if (reasonLower === "exercise_finalized") {
      return t("kotMatchAbandonedExerciseFinalized", "Exercise finalized");
    }
    return null;
  }
  if (row.decided_by) {
    switch (row.decided_by) {
      case "GOLDEN_POINT":
        return t("kotMatchDecidedGoldenPoint", "Decided by Golden Point");
      case "STOP_SCORE_LEAD":
        return null;
      case "STOP_DEADLINE_RANKING":
        return null;
      case "FULL_SET":
        return t("kotMatchDecidedFullSet", "Finished normally (Full set)");
      default:
        return null;
    }
  }
  return null;
}

function KotClassementPanel({
  title,
  rows,
  highlightPlayerId,
  renderValue,
  showTitle = true,
}) {
  if (!rows.length) return null;

  return (
    <>
      {showTitle && title ? (
        <h4 className="kot-classement__title">{title}</h4>
      ) : null}
      <ol className="kot-classement__list">
        {rows.map((row, idx) => {
          const isMe =
            highlightPlayerId &&
            String(row.player_id) === String(highlightPlayerId);
          const name = formatKotPlayerDisplayName(row);
          const clubLabel = formatClubRankingLabel(
            row.fltt_classement,
            row.fltt_ranking_points
          );

          return (
            <li
              key={row.kot_score_id || row.player_id || idx}
              className={`kot-classement__row${isMe ? " kot-classement__row--me" : ""}`}
            >
              <span className={`kot-classement__rank${idx < 3 ? ` kot-classement__rank--${idx + 1}` : ""}`}>
                {idx + 1}
              </span>
              <div className="kot-classement__player">
                <span className="kot-classement__name">{name}</span>
                {clubLabel ? (
                  <span className="kot-classement__club">{clubLabel}</span>
                ) : null}
              </div>
              <span className="kot-classement__value">{renderValue(row)}</span>
            </li>
          );
        })}
      </ol>
    </>
  );
}

const CLASSEMENT_VIEW_KOT = "kot-points";
const CLASSEMENT_VIEW_SPREAD = "point-spread";

function KotFinalClassement({
  kotPointsRanking,
  spreadRanking,
  exerciseMatches,
  highlightPlayerId,
  t,
}) {
  const hasKotRanking = kotPointsRanking.length > 0;
  const hasSpreadRanking = spreadRanking.length > 0;
  const showTags = hasKotRanking && hasSpreadRanking;

  const [classementView, setClassementView] = useState(
    hasKotRanking ? CLASSEMENT_VIEW_KOT : CLASSEMENT_VIEW_SPREAD
  );

  if (!hasKotRanking && !hasSpreadRanking) return null;

  const activeView = showTags
    ? classementView
    : hasKotRanking
      ? CLASSEMENT_VIEW_KOT
      : CLASSEMENT_VIEW_SPREAD;

  return (
    <section className="kot-classement">
      <h4 className="kot-classement__title">
        {t("kotFinalClassementTitle", "Final ranking")}
      </h4>

      {showTags ? (
        <div className="kot-classement__tags" role="tablist" aria-label={t("kotFinalClassementTitle", "Final ranking")}>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === CLASSEMENT_VIEW_KOT}
            className={`kot-classement__tag${activeView === CLASSEMENT_VIEW_KOT ? " kot-classement__tag--active" : ""}`}
            onClick={() => setClassementView(CLASSEMENT_VIEW_KOT)}
          >
            {t("kotClassementTagKotPoints", "KoT points")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === CLASSEMENT_VIEW_SPREAD}
            className={`kot-classement__tag${activeView === CLASSEMENT_VIEW_SPREAD ? " kot-classement__tag--active" : ""}`}
            onClick={() => setClassementView(CLASSEMENT_VIEW_SPREAD)}
          >
            {t("kotClassementTagGameDiff", "Game diff.")}
          </button>
        </div>
      ) : null}

      {activeView === CLASSEMENT_VIEW_KOT && hasKotRanking ? (
        <KotClassementPanel
          rows={kotPointsRanking}
          highlightPlayerId={highlightPlayerId}
          showTitle={false}
          renderValue={(row) => (
            <span className="kot-classement__value-main">
              <span>
                {Number(row.total_points) || 0}
                <span className="kot-classement__value-unit" style={{ marginLeft: "3px" }}>
                  {t("kotPlayerMatchPoints", "Pts")}
                </span>
              </span>
              {row.matches_won > 0 ? (
                <span className="kot-classement__value-sub">
                  {t("kotClassementWinsShort", "{{count}} win(s)", {
                    count: row.matches_won,
                  })}
                </span>
              ) : null}
            </span>
          )}
        />
      ) : null}

      {activeView === CLASSEMENT_VIEW_SPREAD && hasSpreadRanking ? (
        <KotClassementPanel
          rows={spreadRanking}
          highlightPlayerId={highlightPlayerId}
          showTitle={false}
          renderValue={(row) => {
            const spread = kotLeaderboardPointSpread(row, exerciseMatches);
            const scored = kotLeaderboardGamePointsScored(row, exerciseMatches);
            const conceded = Math.max(0, scored - spread);
            return (
              <span className="kot-classement__value-main kot-classement__value-main--spread">
                <span>{spread > 0 ? `+${spread}` : spread}</span>
                <span className="kot-classement__value-sub">
                  {t("kotPointSpreadDetail", "{{scored}}−{{conceded}}", {
                    scored,
                    conceded,
                  })}
                </span>
              </span>
            );
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Mobile-first KoT perf tab — final classements + match cards.
 */
export function KotPlayerMatchList({
  rows,
  leaderboard = [],
  exerciseMatches = [],
  highlightPlayerId = null,
  onPlayVideo,
}) {
  const { t } = useTranslation();

  const kotPointsRanking = useMemo(
    () => sortKotLeaderboardByKotPoints(leaderboard),
    [leaderboard]
  );

  const spreadRanking = useMemo(
    () => sortKotLeaderboardByPointSpread(leaderboard, exerciseMatches),
    [leaderboard, exerciseMatches]
  );

  if (!rows?.length && !kotPointsRanking.length) return null;

  const wins = rows.filter((r) => r.outcome === "win").length;

  return (
    <div className="kot-match-list">
      <KotFinalClassement
        kotPointsRanking={kotPointsRanking}
        spreadRanking={spreadRanking}
        exerciseMatches={exerciseMatches}
        highlightPlayerId={highlightPlayerId}
        t={t}
      />

      {rows.length > 0 ? (
        <>
          <div className="kot-match-list__hero">
            <div className="kot-match-list__hero-icon" aria-hidden>
              <Swords size={18} strokeWidth={2.25} />
            </div>
            <div className="kot-match-list__hero-text">
              <span className="kot-match-list__title">
                {t("kotPlayerMatchHistory", "Match history")}
              </span>
              <span className="kot-match-list__subtitle">
                {t("kotMatchesPlayedSummary", "{{count}} match(es) · {{wins}} win(s)", {
                  count: rows.length,
                  wins,
                })}
              </span>
            </div>
            <div
              className="kot-match-list__count-pill"
              aria-label={t("kotRoundsPlayed", "Rounds played")}
            >
              {rows.length}
            </div>
          </div>

          <ul className="kot-match-list__cards">
            {rows.map((row) => {
              const isWin = row.outcome === "win";
              const roundText = row.round_number != null ? `${t("round", "Round")} #${row.round_number}` : "—";
              const tableText = row.table_number != null ? `T${row.table_number}` : "—";
              const roundTableLabel = row.round_number != null && row.table_number != null
                ? `${roundText} - ${tableText}`
                : `${roundText} · ${tableText}`;

              const hasVideo = isPlayableKotMatchVideoUrl(row.video_url);

              const handlePlayVideo = (event) => {
                event.stopPropagation();
                if (!hasVideo) return;
                onPlayVideo?.(row.video_url, {
                  title: t("kotMatchCardPlayAria", "Watch match vs {{opponent}}", {
                    opponent: row.opponent_name,
                  }),
                  messageId: row.video_message_id || null,
                  kotMatchId: row.kot_match_id || null,
                  viewSetup: row.video_view_setup ?? null,
                });
              };

              return (
                <li
                  key={row.kot_match_id}
                  className={`kot-match-card kot-match-card--${row.outcome || "unknown"}`}
                >
                  {/* Line 1: Round/Table (Left) and Outcome Pill (Right) */}
                  <div className="kot-match-card__line-row kot-match-card__line-row--1">
                    <span className="kot-match-card__round-label">
                      {roundTableLabel}
                    </span>
                    <span
                      className={`kot-match-card__outcome-pill kot-match-card__outcome-pill--${row.outcome}`}
                    >
                      {isWin && <Trophy size={11} strokeWidth={2.5} aria-hidden />}
                      {outcomeLabel(row, t)}
                    </span>
                  </div>

                  {/* Line 2: Vs. Opponent (Left) and Ranking Pill (Right) */}
                  <div className="kot-match-card__line-row kot-match-card__line-row--2">
                    <div className="kot-match-card__opponent-vs">
                      <span className="kot-match-card__opponent-vs-prefix">{t("vs", "Vs.")}</span>
                      <span className="kot-match-card__opponent-vs-name">{row.opponent_name}</span>
                    </div>
                    {row.opponent_ranking_label ? (
                      <span className="kot-match-card__opponent-ranking">
                        {row.opponent_ranking_label}
                      </span>
                    ) : null}
                  </div>

                  {/* Line 3: Score (Left) and Points Won (Right) */}
                  <div className="kot-match-card__line-row kot-match-card__line-row--3">
                    <div className="kot-match-card__score-container">
                      <span className="kot-match-card__score-title">{t("score", "Score")}. </span>
                      <span className="kot-match-card__score-display">{row.score_label}</span>
                    </div>
                    <div className="kot-match-card__points-container">
                      <span className="kot-match-card__points-title">
                        {t("kotPlayerMatchPointsWon", "Points won")}:{" "}
                      </span>
                      {row.points != null ? (
                        <div className={`kot-match-card__points-badge-new${row.points_is_loss ? " kot-match-card__points-badge-new--loss" : ""}`}>
                          {row.points_is_loss ? "" : "+"}{row.points} {t("kotPlayerMatchPoints", "Pts")}
                        </div>
                      ) : (
                        <div className="kot-match-card__points-badge-new kot-match-card__points-badge-new--none">—</div>
                      )}
                    </div>
                  </div>

                  {hasVideo ? (
                    <div className="kot-match-card__line-row kot-match-card__line-row--4">
                      <button
                        type="button"
                        className="kot-match-card__video-btn"
                        aria-label={t("kotMatchCardPlayAria", "Watch match vs {{opponent}}", {
                          opponent: row.opponent_name,
                        })}
                        onClick={handlePlayVideo}
                      >
                        <Clapperboard size={16} className="kot-match-card__video-btn-icon" aria-hidden />
                        <span className="kot-match-card__video-btn-label">
                          {t("watchMatchVideo", "Watch match")}
                        </span>
                      </button>
                    </div>
                  ) : null}

                  {/* Match Message Sub-row if any */}
                  {(() => {
                    const matchMessage = getMatchMessage(row, t);
                    return matchMessage ? (
                      <div className="kot-match-card__message-bubble">
                        {matchMessage}
                      </div>
                    ) : null;
                  })()}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}

export default KotPlayerMatchList;
